import React from 'react';
import PropTypes from 'prop-types';

import Card from '../Card';
import Button from '../Button';
import TextButton from '../TextButton';
import Text from '../Text';
import Heading from '../Heading';
import Proportion from '../Proportion';
import Tooltip from '../Tooltip';
import MediaOverlay from '../MediaOverlay';
import { DataHook } from './constants';
import { classes, st } from './CardGalleryItem.st.css';
import { WixStyleReactContext } from '../WixStyleReactProvider/context';
import { DragHandle } from '../DragHandle/DragHandle';
import Image from '../Image';
import { DraggableContainer } from '../DraggableContainer/DraggableContainer';

class CardGalleryItem extends React.PureComponent {
  static displayName = 'CardGalleryItem';
  static propTypes = {
    /** Accepts a <Badge/> or any other static indicator. Passed element will be displayed at the top left corner of a card. */
    badge: PropTypes.node,

    /** Set card size. */
    size: PropTypes.oneOf(['small', 'medium']),

    /** Set card title. */
    title: PropTypes.node,

    /** Set card subtitle. */
    subtitle: PropTypes.node,

    /** Accepts any component to be rendered as a suffix of the card's footer */
    suffix: PropTypes.node,

    /**
     Show drag handle
     */
    draggable: PropTypes.bool,

    /**
     * Removes buttons when other element is dragging and this card is droppable
     */
    droppable: PropTypes.bool,

    /**
     Extra props to pass to the `<DragHandle />` element
     */
    dragHandleProps: PropTypes.object,

    /**
     * Indicates the element is dragging
     */
    dragging: PropTypes.bool,

    /**
     Show disabled drag handle
     */
    dragDisabled: PropTypes.bool,

    /** Specifies the tab order of the component. */
    tabIndex: PropTypes.number,

    /** Specifies background image URL. */
    backgroundImageUrl: PropTypes.string,

    /** Accepts any component to be rendered as a background image. */
    backgroundImageNode: PropTypes.node,

    /** Defines properties for the primary action button. */
    primaryActionProps: PropTypes.shape({
      /** Label of primary action button */
      label: PropTypes.node,
      /** On click handler of primary action button and of the whole card */
      onClick: PropTypes.func,
      /** Disable the primary action button */
      disabled: PropTypes.bool,
      /** Message to be displayed when primary action button is disabled */
      disabledMessage: PropTypes.string,
    }).isRequired,

    /** Defines properties for the secondary action button. */
    secondaryActionProps: PropTypes.shape({
      /** Label of secondary action text button */
      label: PropTypes.node,
      /** On click handler of secondary action text button */
      onClick: PropTypes.func,
    }),

    /** Defines a popover menu to be displayed on hover at the top right corner or a card. */
    settingsMenu: PropTypes.node,

    /** Applies a data-hook HTML attribute that can be used in the tests. */
    dataHook: PropTypes.string,
    /**
     * Recommended values:
     * - Square - 1
     * - Portrait - 3/4
     * - Cinema - 16/9
     * - Landscape - 4/3
     * - Custom number (e.g. 1.5)
     */
    aspectRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    /** Sets placement of background image. */
    imagePlacement: PropTypes.oneOf(['top', 'side']),

    /** Sets suffix behavior to appear only on card hover. */
    showSuffixOnHover: PropTypes.bool,
  };

  static defaultProps = {
    aspectRatio: 1.6,
    primaryActionProps: {
      onClick: () => {},
    },
    size: 'medium',
    imagePlacement: 'top',
    showSuffixOnHover: false,
  };

  state = {
    isHovered: false,
    isFocused: false,
  };

  _onMouseEnter = () => {
    this.setState({ isHovered: true });
  };

  _onMouseLeave = () => {
    this.setState({ isHovered: false });
  };

  _onFocus = e => {
    try {
      if (
        process.env.NODE_ENV === 'test' ||
        e.target.matches(':focus-visible')
      ) {
        this.setState({ isFocused: true });
      }
    } catch {
      this.setState({ isFocused: true });
    }
  };

  _onBlur = () => {
    this.setState({ isFocused: false });
  };

  _hasFooter() {
    const { title, subtitle, suffix, imagePlacement } = this.props;
    return !!(title || subtitle || suffix || imagePlacement === 'side');
  }

  _renderBadge() {
    return (
      <div className={classes.badgeWrapper} data-hook={DataHook.Badge}>
        {this.props.badge}
      </div>
    );
  }

  _renderDragHandle() {
    const { dragHandleProps, dragging, dragDisabled } = this.props;

    return (
      <DragHandle
        {...dragHandleProps}
        dragging={dragging}
        disabled={dragDisabled}
        dataHook={DataHook.dragHandle}
      />
    );
  }

  _renderFooter() {
    const {
      title,
      subtitle,
      size,
      suffix,
      draggable,
      imagePlacement,
      backgroundImageNode,
      backgroundImageUrl,
      settingsMenu,
    } = this.props;

    const heading =
      size === 'medium' && imagePlacement !== 'side' ? (
        <Heading size="small" ellipsis dataHook={DataHook.Title}>
          {title}
        </Heading>
      ) : (
        <Text size="small" weight="normal" ellipsis dataHook={DataHook.Title}>
          {title}
        </Text>
      );

    const textSize =
      imagePlacement === 'side' ? 'tiny' : size === 'medium' ? 'small' : 'tiny';

    return (
      <>
        {imagePlacement === 'top' && <Card.Divider />}
        <div className={classes.footer} data-hook={DataHook.footer}>
          {draggable && (
            <div className={classes.dragHandleWrapper}>
              {this._renderDragHandle()}
            </div>
          )}
          {imagePlacement === 'side' && (
            <div
              className={st(classes.footerImage, { size })}
              data-hook={DataHook.footerImage}
            >
              {backgroundImageUrl ? (
                <Image src={backgroundImageUrl} />
              ) : (
                backgroundImageNode
              )}
            </div>
          )}
          <div className={classes.footerContent}>
            {heading}
            <Text
              size={textSize}
              secondary
              ellipsis
              dataHook={DataHook.Subtitle}
            >
              {subtitle}
            </Text>
          </div>
          {suffix && (
            <div className={classes.footerSuffix} data-hook={DataHook.suffix}>
              {suffix}
            </div>
          )}
          {imagePlacement === 'side' &&
            settingsMenu &&
            this._renderSettingsMenu()}
        </div>
      </>
    );
  }

  _renderActions() {
    const {
      primaryActionProps: { label, disabled, disabledMessage },
      secondaryActionProps,
      draggable,
    } = this.props;

    const primaryAction = (
      <WixStyleReactContext.Consumer>
        {({ newColorsBranding }) => (
          <Button
            dataHook={DataHook.PrimaryAction}
            disabled={disabled}
            skin={newColorsBranding ? 'light' : 'standard'}
            onPointerDown={draggable ? e => e.stopPropagation() : undefined}
          >
            {label}
          </Button>
        )}
      </WixStyleReactContext.Consumer>
    );

    return (
      <div className={classes.primaryAction} data-hook={DataHook.HoverContent}>
        {disabled && disabledMessage ? (
          <Tooltip disabled={!disabled} content={disabledMessage}>
            {primaryAction}
          </Tooltip>
        ) : (
          primaryAction
        )}

        {secondaryActionProps && (
          <div className={classes.secondaryAction}>
            <TextButton
              skin="light"
              onClick={event => {
                if (secondaryActionProps.onClick) {
                  secondaryActionProps.onClick(event);
                }
                event.stopPropagation();
              }}
              dataHook={DataHook.SecondaryAction}
              onPointerDown={draggable ? e => e.stopPropagation() : undefined}
            >
              {secondaryActionProps.label}
            </TextButton>
          </div>
        )}
      </div>
    );
  }

  _renderSettingsMenu() {
    return (
      <div className={classes.settingsMenu} data-hook={DataHook.SettingsMenu}>
        {this.props.settingsMenu}
      </div>
    );
  }

  render() {
    const {
      primaryActionProps,
      secondaryActionProps,
      dataHook,
      badge,
      backgroundImageUrl,
      backgroundImageNode,
      settingsMenu,
      size,
      draggable,
      dragging,
      droppable,
      dragDisabled,
      tabIndex,
      aspectRatio,
      imagePlacement,
      showSuffixOnHover,
      suffix,
    } = this.props;

    const withFooter = !!this._hasFooter();
    const showActions =
      !dragging && !droppable && !!(primaryActionProps || secondaryActionProps);
    const withFloatingDragHandle = !!draggable && !withFooter;
    const withHover = showActions || withFloatingDragHandle;

    return (
      <DraggableContainer
        draggable={draggable && !showActions}
        dragging={dragging}
        dim
        dragDisabled={dragDisabled}
        tabIndex={tabIndex}
        className={st(classes.root, {
          withFooter,
          size,
          dragging,
          draggable,
          showActions,
          imagePlacement,
          showSuffixOnHover,
          suffix: !!suffix,
        })}
        data-hook={dataHook}
        onFocus={this._onFocus}
        onBlur={this._onBlur}
      >
        <div
          onMouseEnter={this._onMouseEnter}
          onMouseLeave={this._onMouseLeave}
          onClick={primaryActionProps.onClick}
          data-hook={DataHook.Container}
        >
          <Card stretchVertically showShadow>
            {imagePlacement === 'top' && (
              <Proportion aspectRatio={aspectRatio}>
                <MediaOverlay
                  media={backgroundImageUrl || backgroundImageNode || ''}
                  className={classes.overlay}
                  hoverSkin={withHover ? 'dark' : undefined}
                  hovered={this.state.isHovered || this.state.isFocused}
                  dataHook={DataHook.HoverComponent}
                >
                  {showActions && (
                    <MediaOverlay.Content visible="hover">
                      {this._renderActions()}
                    </MediaOverlay.Content>
                  )}
                  {settingsMenu && showActions && (
                    <MediaOverlay.Content visible="hover" placement="top-end">
                      {this._renderSettingsMenu()}
                    </MediaOverlay.Content>
                  )}
                </MediaOverlay>
                {badge && this._renderBadge()}
                <div className={classes.dragHandleFloatingWrapper}>
                  {withFloatingDragHandle && this._renderDragHandle()}
                </div>
              </Proportion>
            )}
            {withFooter && this._renderFooter()}
          </Card>
        </div>
      </DraggableContainer>
    );
  }
}

export default CardGalleryItem;
